"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { SidebarProvider } from "./components/ui/sidebar";
import { AppSidebar } from "./components/AppSidebar";
import { AppRoutes } from "./components/AppRoutes";
import type { UsersOutput } from "./interfaces/users";
import { UsersService } from "./services/UserService";
import { OrganizationService } from "./services/OrganizationService";
import type { OrganizationsOutput } from "./interfaces";
import { AppManager } from "./services/AppManager";
import LandingPage from "./pages/landing/LandingPage";
import Organizations from "@/pages/organizations/Organizations";
import { AppManagerProvider, useAppManager } from "./context/AppManagerContext";
import LeadGenerationForm from "./pages/landing/LeadGeneration";
import OrganizationSetup from "./pages/organizationSetup/OrganizationSetup";
import TermsOfUse from "./components/TermsOfUse";

const ProtectedContent: React.FC<{
  user: UsersOutput;
  haveOrgs: boolean;
  defaultOpen: boolean;
}> = ({ user, defaultOpen }) => {
  const { currentOrganization } = useAppManager();
  const [isMounted, setIsMounted] = useState(false);

  // Garantir que o componente só renderize no cliente para evitar problemas de hidratação
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SidebarProvider defaultOpen={defaultOpen}>
        <AppSidebar
          organization={currentOrganization!}
          user={{
            name: user?.name ?? "Guest",
            avatar: user?.picture ?? "",
            email: user?.email ?? "",
          }}
        />
        <main className="flex-1 overflow-x-hidden">
          <AppRoutes />
        </main>
      </SidebarProvider>
    </div>
  );
};

const App: React.FC = () => {
  const {
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAuth0();
  const [user, setUser] = useState<UsersOutput | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationsOutput[]>([]);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const usersService = new UsersService();
  const organizationService = new OrganizationService();

  const getDefaultSidebarState = () => {
    const cookies = document.cookie.split("; ").reduce((acc, current) => {
      const [key, value] = current.split("=");
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>);

    return cookies["sidebar:state"] === "true";
  };

  useEffect(() => {
    const fetchUserAndOrganizations = async () => {
      if (!isLoading && isAuthenticated) {
        try {
          await getAccessTokenSilently({ cacheMode: "off" });
          const tokenClaim = await getIdTokenClaims();
          const rawToken = tokenClaim?.__raw || "";

          AppManager.getInstance().setToken(rawToken);
          sessionStorage.setItem("userId", tokenClaim?.sub || "");

          const fetchedUser = await usersService.getUserMe();
          setUser(fetchedUser);

          const fetchedOrganizations = await organizationService.getByUsers(
            fetchedUser.userId,
            { expand: "plan" }
          );
          AppManager.getInstance().setOrganizations(fetchedOrganizations);
          setOrganizations(fetchedOrganizations);
        } finally {
          setIsAppLoading(false);
        }
      } else if (!isLoading) {
        setIsAppLoading(false);
      }
    };

    fetchUserAndOrganizations();
  }, [isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently]);

  const defaultOpen = getDefaultSidebarState();

  if (isLoading || isAppLoading) {
    return <LoadingSpinner />;
  }

  // Este código agora apenas configura o roteamento, não retorna diretamente o LandingPage
  return (
    <AppManagerProvider>
      <Router>
        <Routes>
          {/* Rota pública para o ebook - acessível sem autenticação */}
          <Route path="/ebook" element={<LeadGenerationForm />} />

          {/* Se não estiver autenticado, mostra a página de landing para as demais rotas */}
          {!isAuthenticated || !user ? (
            <Route path="*" element={<LandingPage />} />
          ) : (
            <>
              {/* Rota para termos de uso - mostrada quando o usuário está autenticado mas não aceitou os termos */}
              {!user.termsOfUseAccepted && (
                <Route path="/termos" element={<TermsOfUse />} />
              )}

              {/* Redireciona para termos se o usuário não aceitou os termos */}
              {!user.termsOfUseAccepted ? (
                <Route path="*" element={<Navigate to="/termos" replace />} />
              ) : (
                <>
                  {/* Rota inicial redireciona para organizações */}
                  <Route
                    path="/"
                    element={<Navigate to="/organizacoes" replace />}
                  />

                  {/* Rotas sem sidebar */}
                  <Route path="/organizacoes" element={<Organizations />} />
                  <Route
                    path="/organizacoes/novo"
                    element={<OrganizationSetup />}
                  />

                  {/* Rotas com sidebar - apenas acessíveis após selecionar uma organização */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedContent
                        user={user}
                        haveOrgs={!!organizations?.length}
                        defaultOpen={defaultOpen}
                      />
                    }
                  />
                </>
              )}
            </>
          )}
        </Routes>
      </Router>
    </AppManagerProvider>
  );
};

export default App;
